/**
 * Connected sign-in accounts — Google, Apple, Microsoft, LinkedIn — on the
 * signed-in person's own account. Settings → Security, both web portals.
 *
 * CONNECT is a round trip: Clerk makes the connection unverified and hands
 * back the provider's consent URL; the person approves there and comes back
 * to `returnUrl`. What they are coming back from is kept for the session, so
 * the page can say "Google is connected" — or why not (the account already
 * belongs to someone else, they said no) — once they are back. A connection
 * left unverified (an abandoned attempt) is re-authorised, not duplicated.
 *
 * DISCONNECT removes one. Signing in stays possible: the instance always
 * accepts a code to the sign-in email, and a password when there is one.
 *
 * Both are sensitive, so both run behind the step-up (`useStepUp`); neither
 * throws — outcomes are a union, and failures carry Clerk's error for the
 * caller to put into words.
 */

import { useUser } from '@clerk/clerk-react';
import {
  SOCIAL_PROVIDER_STRATEGY,
  SOCIAL_PROVIDERS,
  socialProviderOf,
  type SocialProvider,
} from '@tonnie/foundation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStepUp } from './useStepUp';

export interface ConnectedAccount {
  id: string;
  provider: SocialProvider;
  /** The address the provider vouches for, when it shares one. */
  email: string | null;
}

export type ConnectionOutcome =
  | { outcome: 'done' }
  /** The person dismissed the step-up. Not an error. */
  | { outcome: 'cancelled' }
  | { outcome: 'failed'; error: unknown };

/** What the person just came back from, once. */
export type ConnectionReturn =
  | { provider: SocialProvider; result: 'connected' }
  | { provider: SocialProvider; result: 'failed'; error: unknown };

const PENDING_KEY = 'tonnie.social.connecting';

function keepPending(provider: SocialProvider): void {
  try {
    sessionStorage.setItem(PENDING_KEY, provider);
  } catch {
    // Storage blocked — the connection still happens; only the "connected"
    // message on return is lost.
  }
}

function takePending(): SocialProvider | null {
  try {
    const value = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    return socialProviderOf(value);
  } catch {
    return null;
  }
}

export function useConnectedAccounts() {
  const { user } = useUser();
  const stepUp = useStepUp();
  const [returned, setReturned] = useState<ConnectionReturn | null>(null);

  const external = useMemo(() => user?.externalAccounts ?? [], [user?.externalAccounts]);

  const accounts = useMemo<ConnectedAccount[]>(
    () =>
      external.flatMap((account) => {
        const provider = socialProviderOf(account.provider);
        return provider && account.verification?.status === 'verified'
          ? [{ id: account.id, provider, email: account.emailAddress || null }]
          : [];
      }),
    [external]
  );

  const available = useMemo(
    () => SOCIAL_PROVIDERS.filter((p) => !accounts.some((a) => a.provider === p)),
    [accounts]
  );

  // Back from a provider's consent screen: say how it went, once.
  const checkedReturn = useRef(false);
  useEffect(() => {
    if (!user || checkedReturn.current) return;
    checkedReturn.current = true;
    const provider = takePending();
    if (!provider) return;
    void user.reload().then((fresh) => {
      const account = fresh.externalAccounts.find((a) => socialProviderOf(a.provider) === provider);
      setReturned(
        account?.verification?.status === 'verified'
          ? { provider, result: 'connected' }
          : {
              provider,
              result: 'failed',
              error: account?.verification?.error ?? new Error(`${provider} was not connected`),
            }
      );
    });
  }, [user]);

  const connect = useCallback(
    async (provider: SocialProvider, returnUrl: string): Promise<ConnectionOutcome> => {
      if (!user) return { outcome: 'failed', error: new Error('No signed-in user') };
      const unfinished = external.find(
        (a) => socialProviderOf(a.provider) === provider && a.verification?.status !== 'verified'
      );
      const started = await stepUp(() =>
        unfinished
          ? unfinished.reauthorize({ redirectUrl: returnUrl })
          : user.createExternalAccount({
              strategy: SOCIAL_PROVIDER_STRATEGY[provider],
              redirectUrl: returnUrl,
            })
      );
      switch (started.outcome) {
        case 'cancelled':
          return { outcome: 'cancelled' };
        case 'failed':
          return { outcome: 'failed', error: started.error };
        case 'completed': {
          const consentUrl = started.value.verification?.externalVerificationRedirectURL;
          if (!consentUrl) {
            return {
              outcome: 'failed',
              error: new Error(`Clerk gave no consent URL for ${provider}`),
            };
          }
          keepPending(provider);
          window.location.assign(consentUrl.href);
          return { outcome: 'done' };
        }
        default: {
          const exhaustive: never = started;
          throw new Error(`Unhandled step-up outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [user, external, stepUp]
  );

  const disconnect = useCallback(
    async (accountId: string): Promise<ConnectionOutcome> => {
      const account = external.find((a) => a.id === accountId);
      if (!account || !user) return { outcome: 'failed', error: new Error('No such connection') };
      const removed = await stepUp(() => account.destroy());
      switch (removed.outcome) {
        case 'completed':
          await user.reload();
          return { outcome: 'done' };
        case 'cancelled':
          return { outcome: 'cancelled' };
        case 'failed':
          return { outcome: 'failed', error: removed.error };
        default: {
          const exhaustive: never = removed;
          throw new Error(`Unhandled step-up outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [external, user, stepUp]
  );

  return {
    accounts,
    available,
    connect,
    disconnect,
    returned,
    clearReturned: useCallback(() => setReturned(null), []),
  };
}
